import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { concatLatestFrom, normalizeHttpError } from '../../../../core/util';
import { AppActions } from '../../../../core/util/store/actions';
import { DocumentsConnector } from '../../connector/documents-connector';
import { DocumentActions } from '../actions';
import { StateWithDocuments } from '../documents-state';
import { DocumentsSelectors } from '../selectors';

@Injectable()
export class DocumentsEffects {
  loadProductDocuments$: Observable<DocumentActions.LoadProductDocumentsSuccess | DocumentActions.LoadProductDocumentsFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType<DocumentActions.LoadProductDocuments>(DocumentActions.DocumentActionTypes.LoadProductDocuments),
        mergeMap((action) => {
          return this.documentsConnector.loadProductDocuments(action.userId, action.productCode, action.children).pipe(
            map((documentsWrapper) => new DocumentActions.LoadProductDocumentsSuccess(action.productCode, documentsWrapper)),
            catchError((error) => of(new DocumentActions.LoadProductDocumentsFail(action.productCode, normalizeHttpError(error))))
          );
        })
      )
    );

  searchConformanceEcSearchParams$: Observable<DocumentActions.SearchConformanceEc | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType<DocumentActions.SearchConformanceEcSearchParams>(
        DocumentActions.DocumentActionTypes.SearchConformanceEcSearchParams
      ),
      map((action: DocumentActions.SearchConformanceEcSearchParams) => action.payload),
      concatLatestFrom((payload) =>
        this.store.select(DocumentsSelectors.shouldSearchConformanceEc(payload.searchParams, payload.oldSearchParams))
      ),
      map(([payload, shouldSearch]) => {
        if (shouldSearch) {
          return new DocumentActions.SearchConformanceEc({ searchParams: payload.searchParams });
        }
        return new AppActions.NoOpAction();
      })
    )
  );

  searchConformanceEc$: Observable<DocumentActions.SearchConformanceEcSuccess | DocumentActions.SearchConformanceEcFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType<DocumentActions.SearchConformanceEc>(DocumentActions.DocumentActionTypes.SearchConformanceEc),
        map((action: DocumentActions.SearchConformanceEc) => action.payload),
        mergeMap((payload) => {
          return this.documentsConnector.conformanceEcSearch(payload.searchParams).pipe(
            map((searchResult) => new DocumentActions.SearchConformanceEcSuccess({ ...payload, searchResult: searchResult })),
            catchError((error) =>
              of(
                new DocumentActions.SearchConformanceEcFail({
                  ...payload,
                  error: normalizeHttpError(error),
                })
              )
            )
          );
        })
      )
    );

  loadConformanceEcBrands$: Observable<
    DocumentActions.LoadConformanceEcBrandsSuccess | DocumentActions.LoadConformanceEcBrandsFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType<DocumentActions.LoadConformanceEcBrands>(DocumentActions.DocumentActionTypes.LoadConformanceEcBrands),
      switchMap((_action: DocumentActions.LoadConformanceEcBrands) => {
        return this.documentsConnector.loadConformanceEcBrands().pipe(
          map((data) => new DocumentActions.LoadConformanceEcBrandsSuccess(data)),
          catchError((error) => of(new DocumentActions.LoadConformanceEcBrandsFail(normalizeHttpError(error))))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private documentsConnector: DocumentsConnector,
    private store: Store<StateWithDocuments>
  ) {}
}
